import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, ArrowLeft, Mail, Lock, User, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: googleLogin, checkAuth, isAuthenticated, user: currentUser, loading: authLoading } = useAuth();
  
  const initialView = searchParams.get('view') || 'login';
  const nextPath = searchParams.get('next') || '';
  // Only allow internal relative paths to prevent open-redirect.
  const safeNext = (nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//')) ? nextPath : '';
  const [view, setView] = useState(initialView);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [verifyHint, setVerifyHint] = useState('');
  const [loginResendOpen, setLoginResendOpen] = useState(false);
  const [loginResendBusy, setLoginResendBusy] = useState(false);
  const [loginResendDone, setLoginResendDone] = useState(false);
  const [loginResendCooldown, setLoginResendCooldown] = useState(0);
  const [loginResendSentAt, setLoginResendSentAt] = useState(null);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail));
      setPendingEmail(email);
      setView('verify');
      toast.success('Account created! Check your email for a verification code.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: pendingEmail, code })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail));
      toast.success('Email verified! Redirecting...');
      await checkAuth();
      const userData = data.user;
      const role = userData?.role;
      navigate(safeNext || (role === 'platform_admin' ? '/platform' : role === 'qa' ? '/qa-queue' : role === 'client' ? '/reports' : '/dashboard'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const res = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail));
      toast.success('New code sent!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setVerifyHint('');
    setLoginResendOpen(false);
    setLoginResendDone(false);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      // Read the body exactly once. Using .text() + JSON.parse (instead of
      // .json()) is bulletproof against the "body stream already read"
      // TypeError that surfaces if any error path tries to consume the
      // response a second time, and lets us tolerate non-JSON bodies (e.g.
      // a 502 HTML page from an upstream proxy) without throwing.
      const rawBody = await res.text();
      let data = {};
      if (rawBody) {
        try { data = JSON.parse(rawBody); } catch { data = {}; }
      }
      if (!res.ok) {
        const detailMsg = formatApiError(data.detail);
        // Surface the new-style 401 verify-your-email guard inline (separate
        // styling from generic invalid-credentials errors).
        if ((res.status === 401 || res.status === 403) &&
            /verify your email/i.test(detailMsg)) {
          setVerifyHint(detailMsg);
          return;
        }
        if (res.status === 403) {
          setPendingEmail(email);
          setView('verify');
          toast.info('Please verify your email first.');
          return;
        }
        throw new Error(detailMsg);
      }
      // Sept 2026 — first-login differentiation: a newly-registered
      // user's first authenticated session should NOT be greeted with
      // "Welcome back". The login response already exposes
      // `data.user.is_first_login` for this exact case.
      if (data.user?.is_first_login) {
        toast.success('Welcome to VOX!');
      } else {
        toast.success('Welcome back!');
      }
      await checkAuth();
      const userData = data.user;

      // If the user signed up via the public Pricing page for a paid plan,
      // PricingPage stored the chosen plan in localStorage with a 24h TTL.
      // On their first login post-verification, resume the Stripe checkout
      // flow they came for instead of dropping them on the dashboard.
      let pendingRaw = null;
      try { pendingRaw = localStorage.getItem('pending_plan'); } catch { /* private mode */ }
      if (pendingRaw && userData?.is_first_login) {
        try {
          const pending = JSON.parse(pendingRaw);
          // Drop expired entries — the link could have been clicked >24h
          // after the original Pricing page interaction.
          if (!pending?.expires_at || pending.expires_at <= Date.now()) {
            try { localStorage.removeItem('pending_plan'); } catch { /* noop */ }
          } else {
            try { localStorage.removeItem('pending_plan'); } catch { /* noop */ }
            if (pending?.price_id) {
              const checkoutRes = await fetch(`${API}/billing/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  plan_id: pending.price_id,
                  billing_cycle: pending.billing_cycle || 'monthly',
                  outlet_count: 1,
                  origin_url: window.location.origin,
                }),
              });
              if (checkoutRes.ok) {
                const checkoutData = await checkoutRes.json();
                if (checkoutData.checkout_url) {
                  window.location.href = checkoutData.checkout_url;
                  return;
                }
              }
              // Checkout failed — restore pending_plan so the user does not
              // have to reselect their plan, surface a clear error, and stop
              // here instead of silently dropping them on the dashboard.
              try { localStorage.setItem('pending_plan', pendingRaw); } catch { /* noop */ }
              toast.error('Unable to complete checkout. Please select your plan from the pricing page.');
              return;
            }
          }
        } catch { /* fall through to dashboard */ }
      }

      navigate(safeNext || (userData?.role === 'platform_admin' ? '/platform' : userData?.role === 'qa' ? '/qa-queue' : userData?.role === 'client' ? '/reports' : '/dashboard'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail));
      setPendingEmail(email);
      setView('reset');
      toast.success('If an account exists, a reset code has been sent.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code, password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail));
      toast.success('Password reset! You can now log in.');
      setView('login');
      setCode('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-redirect when an already-signed-in user lands on /auth (e.g. via the
  // revision email's ?next= link). Honours `safeNext` first, otherwise routes
  // by role.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    const role = currentUser?.role;
    navigate(safeNext || (role === 'platform_admin' ? '/platform' : role === 'qa' ? '/qa-queue' : role === 'client' ? '/reports' : '/dashboard'), { replace: true });
  }, [authLoading, isAuthenticated, currentUser?.role, safeNext, navigate]);

  // Tick the resend cooldown every second so users see the countdown.
  useEffect(() => {
    if (loginResendCooldown <= 0) return undefined;
    const t = setInterval(() => setLoginResendCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [loginResendCooldown]);

  const handleLoginResend = async (resendEmail) => {
    if (loginResendCooldown > 0) return;
    const trimmed = (resendEmail || '').trim();
    if (!trimmed) return;
    setLoginResendBusy(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
    } catch {
      // Endpoint always returns 200 — surface the same outcome either way.
    } finally {
      setLoginResendBusy(false);
      setLoginResendDone(true);
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      setLoginResendSentAt(`${hh}:${mm}`);
      setLoginResendCooldown(60);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="auth-page">
      {/* Header */}
      <header className="py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="auth-logo">
            <img src="/satisfind-logo.png" alt="satisFIND" style={{ width: 160, height: 'auto' }} />
          </a>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* LOGIN */}
          {view === 'login' && (
            <Card className="border-[#E2E8F0] shadow-lg" data-testid="login-card">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-['Montserrat'] text-[#0F172A]">Welcome back</CardTitle>
                <p className="text-sm text-[#64748B] mt-1">Sign in to your satisFIND&reg; account</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {verifyHint && (
                  <div
                    className="flex flex-col gap-2 rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-3 py-2 text-sm text-[#92400E]"
                    data-testid="verify-email-hint"
                  >
                    <span className="font-medium">{verifyHint}</span>
                    {loginResendDone ? (
                      <div className="space-y-0.5" data-testid="login-resend-done">
                        <span className="text-[#15803D] text-xs block">
                          If an account exists for that email, a new verification link has been sent.
                        </span>
                        {loginResendSentAt && (
                          <span className="text-[#64748B] text-[11px] block" data-testid="login-resend-sent-at">
                            Sent at {loginResendSentAt}
                            {loginResendCooldown > 0 && <> · You can resend again in {loginResendCooldown}s</>}
                          </span>
                        )}
                      </div>
                    ) : loginResendOpen ? (
                      <form
                        onSubmit={(e) => { e.preventDefault(); handleLoginResend(email); }}
                        className="flex items-center gap-2"
                        data-testid="login-resend-form"
                      >
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          required
                          className="h-8 text-sm"
                          data-testid="login-resend-email"
                        />
                        <Button
                          type="submit"
                          disabled={loginResendBusy || loginResendCooldown > 0}
                          className="bg-[#1A3E6F] hover:bg-[#122c50] text-white h-8 text-xs px-3 disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid="login-resend-submit"
                        >
                          {loginResendBusy ? 'Sending…' : loginResendCooldown > 0 ? `Resend in ${loginResendCooldown}s` : 'Resend'}
                        </Button>
                      </form>
                    ) : (
                      <button
                        type="button"
                        className="text-left text-xs text-[#1A3E6F] hover:underline"
                        onClick={() => setLoginResendOpen(true)}
                        data-testid="login-resend-link"
                      >
                        Resend verification email
                      </button>
                    )}
                  </div>
                )}
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-[#0F172A] text-sm font-medium">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="you@company.com" className="pl-10" required data-testid="login-email" />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between">
                      <Label htmlFor="password" className="text-[#0F172A] text-sm font-medium">Password</Label>
                      <button type="button" onClick={() => setView('forgot')} className="text-xs text-[#1A3E6F] hover:underline" data-testid="forgot-password-link">
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                        onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters"
                        className="pl-10 pr-10" required data-testid="login-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                    className="w-full bg-[#1A3E6F] hover:bg-[#122c50] font-['Montserrat'] font-semibold py-5">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* REGISTER */}
          {view === 'register' && (
            <Card className="border-[#E2E8F0] shadow-lg" data-testid="register-card">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-['Montserrat'] text-[#0F172A]">Create your account</CardTitle>
                <p className="text-sm text-[#64748B] mt-1">Start your free trial today</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-[#0F172A] text-sm font-medium">Full Name</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input id="name" value={name} onChange={e => setName(e.target.value)}
                        placeholder="Your name" className="pl-10" required data-testid="register-name" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="reg-email" className="text-[#0F172A] text-sm font-medium">Work Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input id="reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="you@company.com" className="pl-10" required data-testid="register-email" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="reg-password" className="text-[#0F172A] text-sm font-medium">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input id="reg-password" type={showPassword ? 'text' : 'password'} value={password}
                        onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters"
                        className="pl-10 pr-10" required minLength={8} data-testid="register-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="register-submit-btn"
                    className="w-full bg-[#1A3E6F] hover:bg-[#122c50] font-['Montserrat'] font-semibold py-5">
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E2E8F0]" /></div>
                  <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[#64748B]">or</span></div>
                </div>

                <Button variant="outline" onClick={googleLogin} data-testid="google-register-btn"
                  className="w-full border-[#E2E8F0] text-[#0F172A] font-medium py-5 hover:bg-[#F8F9FA]">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Sign Up with Google
                </Button>

                <p className="text-center text-sm text-[#64748B] mt-4">
                  Already have an account?{' '}
                  <button onClick={() => setView('login')} className="text-[#1A3E6F] font-semibold hover:underline" data-testid="go-to-login">
                    Sign In
                  </button>
                </p>
              </CardContent>
            </Card>
          )}

          {/* VERIFY EMAIL */}
          {view === 'verify' && (
            <Card className="border-[#E2E8F0] shadow-lg" data-testid="verify-card">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-['Montserrat'] text-[#0F172A]">Verify your email</CardTitle>
                <p className="text-sm text-[#64748B] mt-1">
                  We sent a 6-digit code to <span className="font-medium text-[#0F172A]">{pendingEmail}</span>
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleVerify} className="space-y-4">
                  <div>
                    <Label htmlFor="verify-code" className="text-[#0F172A] text-sm font-medium">Verification Code</Label>
                    <Input id="verify-code" value={code} onChange={e => setCode(e.target.value)}
                      placeholder="Enter 6-digit code" className="text-center text-xl tracking-[0.3em] font-['Montserrat'] font-bold mt-1"
                      maxLength={6} required data-testid="verify-code-input" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="verify-submit-btn"
                    className="w-full bg-[#1A3E6F] hover:bg-[#122c50] font-['Montserrat'] font-semibold py-5">
                    {loading ? 'Verifying...' : 'Verify Email'}
                  </Button>
                </form>
                <p className="text-center text-sm text-[#64748B]">
                  Didn't get the code?{' '}
                  <button onClick={handleResendCode} className="text-[#1A3E6F] font-semibold hover:underline" data-testid="resend-code-btn">
                    Resend
                  </button>
                </p>
                <button onClick={() => setView('login')} className="flex items-center gap-1 text-sm text-[#64748B] hover:text-[#1A3E6F] mx-auto">
                  <ArrowLeft className="w-3 h-3" /> Back to Login
                </button>
              </CardContent>
            </Card>
          )}

          {/* FORGOT PASSWORD */}
          {view === 'forgot' && (
            <Card className="border-[#E2E8F0] shadow-lg" data-testid="forgot-card">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-['Montserrat'] text-[#0F172A]">Forgot password?</CardTitle>
                <p className="text-sm text-[#64748B] mt-1">Enter your email and we'll send a reset code</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="forgot-email" className="text-[#0F172A] text-sm font-medium">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input id="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="you@company.com" className="pl-10" required data-testid="forgot-email-input" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                    className="w-full bg-[#1A3E6F] hover:bg-[#122c50] font-['Montserrat'] font-semibold py-5">
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </form>
                <button onClick={() => setView('login')} className="flex items-center gap-1 text-sm text-[#64748B] hover:text-[#1A3E6F] mx-auto" data-testid="back-to-login">
                  <ArrowLeft className="w-3 h-3" /> Back to Login
                </button>
              </CardContent>
            </Card>
          )}

          {/* RESET PASSWORD */}
          {view === 'reset' && (
            <Card className="border-[#E2E8F0] shadow-lg" data-testid="reset-card">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-['Montserrat'] text-[#0F172A]">Reset password</CardTitle>
                <p className="text-sm text-[#64748B] mt-1">
                  Enter the code sent to <span className="font-medium text-[#0F172A]">{pendingEmail}</span>
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="reset-code" className="text-[#0F172A] text-sm font-medium">Reset Code</Label>
                    <Input id="reset-code" value={code} onChange={e => setCode(e.target.value)}
                      placeholder="Enter 6-digit code" className="text-center text-xl tracking-[0.3em] font-['Montserrat'] font-bold mt-1"
                      maxLength={6} required data-testid="reset-code-input" />
                  </div>
                  <div>
                    <Label htmlFor="new-password" className="text-[#0F172A] text-sm font-medium">New Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input id="new-password" type={showPassword ? 'text' : 'password'} value={newPassword}
                        onChange={e => setNewPassword(e.target.value)} placeholder="Min. 8 characters"
                        className="pl-10 pr-10" required minLength={8} data-testid="reset-new-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                    className="w-full bg-[#1A3E6F] hover:bg-[#122c50] font-['Montserrat'] font-semibold py-5">
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
                <button onClick={() => setView('login')} className="flex items-center gap-1 text-sm text-[#64748B] hover:text-[#1A3E6F] mx-auto">
                  <ArrowLeft className="w-3 h-3" /> Back to Login
                </button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
